import { type FunctionComponent, type ReactNode, useState } from 'react';

import { Link, WithTooltip } from '@dr.pogodin/react-utils';

import Throbber from 'components/Throbber';
import { AdminPage } from 'components/admin';
import AddNewButton from 'components/admin/AddNewButton';

import useResearchLibCategories from 'hooks/researchLib/useResearchLibCategories';
import useResearchLibLinks from 'hooks/researchLib/useResearchLibLinks';

import { type ResearchLibCategoryT, type ResearchLibLinkT, getId } from 'utils';

import IconEdit from 'assets/images/icon-edit-16px.svg';

import IconTrash from 'assets/images/icon-trash-16px.svg';

import EditCategoryModal from './EditCategoryModal';
import EditLinkModal from './EditLinkModal';

import { RemoveCategoryModal } from './RemoveCategoryModal';
import { RemoveLinkModal } from './RemoveLinkModal';

import S from './style.scss';

type ModalT = ['CATEGORY', ResearchLibCategoryT | null]
  | ['LINK', ResearchLibLinkT | null]
  | ['REMOVE_CATEGORY', string]
  | ['REMOVE_LINK', string]
  | false;

const ResearchLib: FunctionComponent = () => {
  const [modal, setModal] = useState<ModalT>(false);

  const categories = useResearchLibCategories();
  const links = useResearchLibLinks();

  const content: ReactNode[] = [];

  for (const cat of categories.items) {
    const catId = getId(cat);

    const catLinks: ResearchLibLinkT[] = [];
    for (const link of links.items) {
      if (link.category === catId) {
        catLinks.push(link);
      }
    }
    catLinks.sort((a, b) => a.text.localeCompare(b.text));

    content.push(
      <div key={catId} styleName="category">
        {cat.name}
        <Link
          onClick={() => {
            setModal(['CATEGORY', cat]);
          }}
          to=""
        >
          <IconEdit />
        </Link>
        {catLinks.length ? (
          <WithTooltip
            tip="Category is not empty"
          >
            <IconTrash styleName="notAllowed" />
          </WithTooltip>
        ) : (
          <Link
            onClick={() => {
              setModal(['REMOVE_CATEGORY', catId]);
            }}
            to=""
          >
            <IconTrash />
          </Link>
        )}
      </div>,
    );

    const linkNodes: ReactNode[] = [];
    for (const link of catLinks) {
      linkNodes.push(
        <li key={getId(link)} styleName="link">
          <Link
            openNewTab
            to={link.url}
          >
            {link.text}
          </Link>
          <Link
            onClick={() => {
              setModal(['LINK', link]);
            }}
            to=""
          >
            <IconEdit />
          </Link>
          <Link
            onClick={() => {
              setModal(['REMOVE_LINK', getId(link)]);
            }}
            to=""
          >
            <IconTrash />
          </Link>
        </li>,
      );
    }

    content.push(
      <ul key={`links/${catId}`}>
        {linkNodes}
      </ul>,
    );
  }

  if (!content.length && categories.loading) {
    content.push(<Throbber key="throbber" />);
  }

  let modalNode: ReactNode;

  if (modal) {
    switch (modal[0]) {
      case 'CATEGORY':
        modalNode = (
          <EditCategoryModal
            initial={modal[1]}
            onClose={() => {
              setModal(false);
            }}
          />
        );
        break;
      case 'LINK':
        modalNode = (
          <EditLinkModal
            initial={modal[1]}
            onClose={() => {
              setModal(false);
            }}
          />
        );
        break;
      case 'REMOVE_CATEGORY':
        modalNode = (
          <RemoveCategoryModal
            catId={modal[1]}
            onClose={() => {
              setModal(false);
            }}
          />
        );
        break;
      case 'REMOVE_LINK':
        modalNode = (
          <RemoveLinkModal
            linkId={modal[1]}
            onCancel={() => {
              setModal(false);
            }}
          />
        );
        break;
      default: throw Error('Unexpected case');
    }
  }

  return (
    <AdminPage
      contentClassName={S.container}
      subTitle="Manage categories & links"
      title="Research Library"
    >
      <div styleName="header">
        <AddNewButton
          label="Add New Category"
          onClick={() => {
            setModal(['CATEGORY', null]);
          }}
        />
        <AddNewButton
          label="Add New Link"
          onClick={() => {
            setModal(['LINK', null]);
          }}
        />
      </div>
      <div styleName="content">
        {content}
      </div>
      {modalNode}
    </AdminPage>
  );
};

export default ResearchLib;
